import { defineStore } from 'pinia';
import { ref } from 'vue';
import { getJob, updateJob, type GetJobResponse } from '../api/jobs.ts';
import RailsUtility from '../utilities/rails.ts';
import DateUtility from '../utilities/dates.ts';
import type { ContactEntity } from '@/api/entity_types/Contact.ts';

const emptyJob: Job = {
  title: '',
  description: '',
  url: '',
  contacts: [],
  cities: [],
  history: [],
};

export const useJobStore = defineStore('job', () => {
  const currentJob = ref<Job>(emptyJob);

  const loadCurrentJob = async (payload: { id: number }) => {
    const data = await getJob({ id: payload.id });
    const parsedJob = parseJob(data);
    currentJob.value = parsedJob;
  };

  const flushCurrentJob = () => {
    currentJob.value = emptyJob;
  };

  const updateCurrentJob = async (payload: { id: number; updatedJobAttributes: any }) => {
    await updateJob({ id: payload.id }, { updatedJobAttributes: payload.updatedJobAttributes });
    if (currentJob.value.id === payload.id) {
      await loadCurrentJob({ id: payload.id });
    }
  };

  return {
    currentJob,
    loadCurrentJob,
    flushCurrentJob,
    updateJob: updateCurrentJob,
  };
});

const parseJob = (data: GetJobResponse) => {
  const activeJobDetails = data.record.job_details.find(detail => detail.status === 'active');
  const draftJobDetails = data.record.job_details.find(detail => detail.status === 'draft');
  const fallbackJobDetails = data.record.job_details[0];
  const currentJobDetails = activeJobDetails || fallbackJobDetails;
  
  const currentJob = {
    id: data.record.id,
    indeedRefId: String(data.record.indeed_ref_id || ''),
    jobType: data.record.job_type,
    status: RailsUtility.convertStatusFromApi(data.record.status),
    jobStatus: RailsUtility.convertStatusFromApi(data.record.status),
    description: currentJobDetails?.description || '',
    title: data.record.title,
    url: data.record.url,
    //@ts-ignore
    userId: data.record.user_id,
    isStale: data.is_stale,
    draftJobDetails: data.draft_job_details || draftJobDetails,
    resumeRequired: data.record.resume_required,
    contacts: data.contacts,
    cities: data.record.job_locations,
    history: [], // Will be set below
  };

  const jobHistory = data.versions.reduce((history, version) => {
    const changes = version.version.object_changes;
    if (changes) {
      Object.entries(changes).forEach(([key, [from, to]]) => {
        if (!['updated_at', 'description', 'syndication_description'].includes(key)) {
          //@ts-ignore
          history.push({
            'updated by': version.whodunnit,
            'field changed': key.replace(/_/g, ' '),
            from,
            to,
            'updated at': DateUtility.formatDate(version.version.created_at, 'MMMM Do yyyy, h:mm a'),
          });
        }
      });
    }
    return history;
  }, []);
  //@ts-ignore
  currentJob.history = jobHistory;
  return currentJob;
};

export type JobHistory = {
  'updated by': string;
  'field changed': string;
  from: unknown;
  to: unknown;
  'updated at': string;
};

export type Job = {
  id?: number;
  indeedRefId?: string;
  jobType?: string;
  status?: string;
  jobStatus?: string;
  description: string;
  title: string;
  url: string;
  userId?: number;
  isStale?: boolean;
  draftJobDetails?: unknown;
  resumeRequired?: boolean;
  contacts: ContactEntity[];
  cities: unknown[];
  history: JobHistory[];
};

export type JobState = {
  currentJob: Job;
};

export type RootState = {
  job: JobState;
};
